/**
 * The single place that talks HTTP. Components never call fetch directly.
 */

export const API_BASE_URL: string =
  import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000/api/v1";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: Record<string, unknown>;

  constructor(status: number, code: string, message: string, detail: Record<string, unknown> = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

export interface Page<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

type QueryValue = string | number | boolean | null | undefined | Array<string | number>;

export function buildQuery(params: Record<string, QueryValue> = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      value.forEach((item) => search.append(key, String(item)));
    } else {
      search.append(key, String(value));
    }
  }
  const query = search.toString();
  return query ? `?${query}` : "";
}

async function parseError(response: Response): Promise<ApiError> {
  let code = "http_error";
  let message = `${response.status} ${response.statusText}`;
  let detail: Record<string, unknown> = {};
  try {
    const body = await response.json();
    const payload = (body?.detail ?? body) as Record<string, unknown>;
    if (payload && typeof payload === "object") {
      code = (payload.code as string) ?? code;
      message = (payload.message as string) ?? JSON.stringify(payload);
      detail = payload;
    } else if (typeof payload === "string") {
      message = payload;
    }
  } catch {
    /* a non-JSON error body keeps the status line as the message */
  }
  return new ApiError(response.status, code, message, detail);
}

/**
 * The access token, held in one place.
 *
 * `localStorage` rather than a cookie: the API is a separate origin, there is no session cookie
 * to piggy-back on, and the token is short-lived. `onUnauthenticated` lets the app react to an
 * expired token in one place instead of every caller checking for a 401.
 */
const TOKEN_KEY = "discovery.accessToken";

let accessToken: string | null =
  typeof localStorage === "undefined" ? null : localStorage.getItem(TOKEN_KEY);
let onUnauthenticated: (() => void) | undefined;

export function setAccessToken(token: string | null): void {
  accessToken = token;
  if (typeof localStorage === "undefined") return;
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setUnauthenticatedHandler(handler: (() => void) | undefined): void {
  onUnauthenticated = handler;
}

/** Endpoints that must work without a token: signing in, and following an invitation link. */
function isPublic(path: string): boolean {
  return (
    path.startsWith("/auth/login") ||
    path.startsWith("/auth/config") ||
    path.startsWith("/auth/invitations/")
  );
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(init.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(init.headers ?? {}),
    },
  });

  if (response.status === 401 && !isPublic(path)) {
    setAccessToken(null);
    onUnauthenticated?.();
  }
  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) return (await response.text()) as unknown as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: body === undefined ? undefined : JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: "POST", body: form }),
  download: (path: string) => `${API_BASE_URL}${path}`,
};
